/**
 * Deudas.
 *
 * Tres cosas que hacen que esta pantalla valga la pena:
 *
 *  · **El pago se desglosa en capital, intereses, seguro y comisiones.** Meter
 *    todo en un solo número es lo que hace que la gente crea que su deuda baja más
 *    de lo que baja. Aquí se ve que el capital es lo único que la reduce.
 *  · **Las deudas en mora congelada no pesan en el flujo del mes**, pero sí suman
 *    al pasivo. Congelarlas sirve para no ahogarse, no para hacerse trampas.
 *  · **El simulador** dice cuándo quedarías libre y compara la bola de nieve con la
 *    avalancha, con una recomendación en lenguaje llano.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Landmark, Plus, Snowflake, Sparkles, TrendingDown } from 'lucide-react';
import { useState } from 'react';

import { formatCOP, formatMonthLabel, parseAmountFromText, todayISO, toLocalTimeHHmm } from '@eldescuadre/core';
import type { Debt } from '@eldescuadre/contracts';

import {
  createDebt,
  deleteDebt,
  getAccounts,
  getDebts,
  getDebtsTotals,
  payDebtInstallment,
  simulateDebts,
  updateDebt,
} from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Card, EmptyState, ErrorNote, Field, Loading, Modal, Money, Note, Stat, SubmitButton } from '../components/ui';

/** Etiqueta y color de cada estado de deuda. */
const STATUS_LABEL: Record<string, { label: string; className: string; help: string }> = {
  CURRENT: { label: 'Al día', className: 'text-ed-success', help: 'Paga su cuota cada mes.' },
  FROZEN_ARREARS: {
    label: 'Congelada',
    className: 'text-ed-cyan-light',
    help: 'En mora y con la cuota detenida mientras negocias. No pesa en el mes, pero sigue sumando a lo que debes.',
  },
  NEGOTIATION: {
    label: 'En negociación',
    className: 'text-ed-gold',
    help: 'Estás hablando con el banco.',
  },
  PAID: { label: 'Saldada', className: 'text-ed-text-muted', help: 'Ya no debes nada.' },
};

const KIND_LABEL: Record<string, string> = {
  CREDIT_CARD: 'Tarjeta de crédito',
  PERSONAL_LOAN: 'Crédito personal',
  STORE_CREDIT: 'Crédito de almacén',
  MORTGAGE: 'Hipoteca',
  VEHICLE: 'Crédito de vehículo',
  FAMILY: 'Préstamo familiar',
  OTHER: 'Otro',
};

export function DebtsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState<Debt | null>(null);
  const [simulating, setSimulating] = useState(false);

  const debts = useQuery({ queryKey: ['debts'], queryFn: getDebts });
  const totals = useQuery({ queryKey: ['debts', 'totals'], queryFn: getDebtsTotals });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['debts'] }),
      queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      queryClient.invalidateQueries({ queryKey: ['summary'] }),
      queryClient.invalidateQueries({ queryKey: ['transactions'] }),
    ]);
  };

  const remove = useMutation({
    mutationFn: deleteDebt,
    onSuccess: refresh,
  });

  const freeze = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateDebt(id, { status: status as 'CURRENT' }),
    onSuccess: refresh,
  });

  const active = (debts.data?.debts ?? []).filter((d) => d.status !== 'PAID');
  const paid = (debts.data?.debts ?? []).filter((d) => d.status === 'PAID');

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Deudas</h1>
          <p className="mt-0.5 text-sm text-ed-text-muted">Lo que debes y cuándo quedarás libre.</p>
        </div>
        <button
          type="button"
          className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4"
          onClick={() => setCreating(true)}
        >
          <Plus size={18} aria-hidden="true" />
          Deuda
        </button>
      </header>

      {totals.isLoading && <Loading label="Sumando tus deudas…" />}

      {totals.data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Stat
              label="Debes en total"
              value={<Money amount={totals.data.totalBalance} />}
              hint={`${totals.data.activeCount} ${totals.data.activeCount === 1 ? 'deuda activa' : 'deudas activas'}`}
              tone="negative"
            />
            <Stat
              label="Cuotas de este mes"
              value={<Money amount={totals.data.monthlyInstallments} />}
              hint={
                totals.data.frozenCount > 0 ? (
                  <>
                    {totals.data.frozenCount}{' '}
                    {totals.data.frozenCount === 1 ? 'deuda congelada' : 'deudas congeladas'} no
                    cuentan aquí
                  </>
                ) : (
                  'Solo las deudas al día'
                )
              }
            />
          </div>

          {active.length > 0 && (
            <button
              type="button"
              className="ed-btn-secondary w-full"
              onClick={() => setSimulating(true)}
            >
              <Sparkles size={16} aria-hidden="true" />
              ¿Cuándo quedaré libre?
            </button>
          )}

          <Card title="Tus deudas">
            {debts.isLoading && <Loading />}

            {active.length === 0 && (
              <EmptyState
                title="No tienes deudas registradas"
                message="Si no debes nada, enhorabuena. Si tienes alguna, añádela y verás cuándo puedes salir de ella."
                action={
                  <button
                    type="button"
                    className="ed-btn-primary !w-auto !px-4"
                    onClick={() => setCreating(true)}
                  >
                    Añadir una deuda
                  </button>
                }
              />
            )}

            <ul className="space-y-3">
              {active.map((debt) => {
                const status = STATUS_LABEL[debt.status] ?? STATUS_LABEL.CURRENT!;
                const isFrozen = debt.status === 'FROZEN_ARREARS';

                return (
                  <li
                    key={debt.id}
                    className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-medium">
                          <Landmark size={15} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
                          <span className="truncate">{debt.entity}</span>
                        </p>
                        <p className="mt-0.5 text-xs text-ed-text-muted">
                          {KIND_LABEL[debt.kind] ?? debt.kind}
                          {' · '}
                          <span className={status.className}>{status.label}</span>
                        </p>
                        {debt.rateEA !== null && (
                          <p className="mt-0.5 text-xs text-ed-text-muted">
                            {debt.rateEA.toFixed(1)} % efectivo anual
                            {debt.installment !== null && <> · cuota {formatCOP(debt.installment)}</>}
                          </p>
                        )}
                      </div>

                      <Money amount={debt.balance} className="shrink-0 font-display font-bold" />
                    </div>

                    {isFrozen && (
                      <p className="mt-2 flex items-start gap-1.5 text-xs text-ed-cyan-light">
                        <Snowflake size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
                        {status.help}
                      </p>
                    )}

                    {debt.settlementTarget !== null && (
                      <p className="mt-2 rounded-ed border border-ed-gold/30 bg-ed-gold/10 px-2.5 py-1.5 text-xs text-ed-gold">
                        Te ofrecen cerrarla por {formatCOP(debt.settlementTarget)}
                        {debt.balance > debt.settlementTarget && (
                          <>
                            {' '}
                            — te ahorrarías {formatCOP(debt.balance - debt.settlementTarget)}
                          </>
                        )}
                        {debt.settlementNote && <> · {debt.settlementNote}</>}
                      </p>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="ed-btn-secondary !min-h-[40px] flex-1 text-xs"
                        onClick={() => setPaying(debt)}
                      >
                        Pagar cuota
                      </button>

                      <button
                        type="button"
                        className="ed-btn-ghost !min-h-[40px] !w-auto !px-3 text-xs"
                        onClick={() =>
                          freeze.mutate({
                            id: debt.id,
                            status: isFrozen ? 'CURRENT' : 'FROZEN_ARREARS',
                          })
                        }
                        title={isFrozen ? 'Reanudar la cuota mensual' : 'Congelar la cuota mientras negocias'}
                      >
                        <Snowflake size={14} aria-hidden="true" />
                        {isFrozen ? 'Reanudar' : 'Congelar'}
                      </button>

                      <button
                        type="button"
                        className="ed-btn-ghost !min-h-[40px] !w-auto !px-3 text-xs text-ed-coral-light"
                        onClick={() => {
                          if (window.confirm(`¿Eliminar la deuda con ${debt.entity}?`)) {
                            remove.mutate(debt.id);
                          }
                        }}
                        aria-label={`Eliminar la deuda con ${debt.entity}`}
                      >
                        Eliminar
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>

          {paid.length > 0 && (
            <Card title="Saldadas">
              <ul className="divide-y divide-ed-bg-border/25">
                {paid.map((debt) => (
                  <li key={debt.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="truncate text-ed-text-muted">{debt.entity}</span>
                    <Money amount={0} className="text-ed-success" />
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Note>
            Congelar una deuda <strong>no la hace desaparecer</strong>: deja de pesar en el mes para
            que puedas respirar mientras negocias, pero sigue contando en lo que debes. Ocultarla
            sería darte una foto falsa.
          </Note>
        </>
      )}

      {creating && (
        <NewDebtModal
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}

      {paying && (
        <PayModal
          debt={paying}
          onClose={() => setPaying(null)}
          onSaved={async () => {
            setPaying(null);
            await refresh();
          }}
        />
      )}

      {simulating && <SimulatorModal onClose={() => setSimulating(false)} />}
    </div>
  );
}

/** Añadir o editar una deuda. */
function NewDebtModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [entity, setEntity] = useState('');
  const [kind, setKind] = useState('PERSONAL_LOAN');
  const [balanceText, setBalanceText] = useState('');
  const [rateText, setRateText] = useState('');
  const [installmentText, setInstallmentText] = useState('');
  const [dueDay, setDueDay] = useState('');

  const balance = parseAmountFromText(balanceText) ?? 0;
  const installment = parseAmountFromText(installmentText);

  const save = useMutation({
    mutationFn: () =>
      createDebt({
        entity: entity.trim(),
        kind: kind as 'PERSONAL_LOAN',
        balance,
        rateEA: rateText.trim() ? Number(rateText.replace(',', '.')) : undefined,
        installment: installment ?? undefined,
        dueDay: dueDay.trim() ? Number(dueDay) : undefined,
        status: 'CURRENT',
        priority: 3,
        arrearsMonths: 0,
      }),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Nueva deuda"
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={entity.trim().length === 0 || balance <= 0}
          onClick={() => save.mutate()}
        >
          Guardar deuda
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Field label="¿Con quién es la deuda?" htmlFor="entidad">
          <input
            id="entidad"
            className="ed-input"
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            placeholder="Banco de Bogotá, Éxito, un familiar…"
            autoFocus
            autoComplete="off"
          />
        </Field>

        <Field label="Tipo" htmlFor="tipo-deuda">
          <select id="tipo-deuda" className="ed-input" value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(KIND_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Cuánto debes hoy" htmlFor="saldo">
          <input
            id="saldo"
            className="ed-input ed-money"
            inputMode="numeric"
            value={balanceText}
            onChange={(e) => setBalanceText(e.target.value)}
            placeholder="2.000.000"
            autoComplete="off"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Interés (% anual)"
            htmlFor="interes"
            hint="Si no lo sabes, déjalo vacío."
          >
            <input
              id="interes"
              className="ed-input"
              inputMode="decimal"
              value={rateText}
              onChange={(e) => setRateText(e.target.value)}
              placeholder="24,5"
              autoComplete="off"
            />
          </Field>

          <Field label="Cuota mensual" htmlFor="cuota">
            <input
              id="cuota"
              className="ed-input ed-money"
              inputMode="numeric"
              value={installmentText}
              onChange={(e) => setInstallmentText(e.target.value)}
              placeholder="400.000"
              autoComplete="off"
            />
          </Field>
        </div>

        <Field
          label="Día de pago (opcional)"
          htmlFor="dia-pago"
          hint="Para poder avisarte antes de que venza."
        >
          <input
            id="dia-pago"
            className="ed-input"
            inputMode="numeric"
            value={dueDay}
            onChange={(e) => setDueDay(e.target.value)}
            placeholder="15"
            autoComplete="off"
          />
        </Field>

        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}

/**
 * Pagar una cuota.
 *
 * El desglose es lo importante: solo el capital baja la deuda. Los intereses, el
 * seguro y las comisiones son gasto del mes.
 */
function PayModal({
  debt,
  onClose,
  onSaved,
}: {
  debt: Debt;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [principalText, setPrincipalText] = useState(debt.installment ? String(debt.installment) : '');
  const [interestText, setInterestText] = useState('');
  const [insuranceText, setInsuranceText] = useState('');
  const [feesText, setFeesText] = useState('');
  const [accountId, setAccountId] = useState('');

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });

  const principal = parseAmountFromText(principalText) ?? 0;
  const interest = parseAmountFromText(interestText) ?? 0;
  const insurance = parseAmountFromText(insuranceText) ?? 0;
  const fees = parseAmountFromText(feesText) ?? 0;

  const total = principal + interest + insurance + fees;
  const balanceAfter = Math.max(0, debt.balance - principal);
  const chosenAccountId = accountId || accounts.data?.accounts[0]?.id || '';

  const problem =
    principal > debt.balance
      ? `El capital no puede superar lo que debes (${formatCOP(debt.balance)}).`
      : total <= 0
        ? 'Indica cuánto vas a pagar.'
        : null;

  const save = useMutation({
    mutationFn: () =>
      payDebtInstallment(
        debt.id,
        {
          principal,
          interest,
          insurance,
          fees,
          date: todayISO(),
          time: toLocalTimeHHmm(new Date()),
          accountId: chosenAccountId,
        },
        crypto.randomUUID(),
      ),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Pagar a ${debt.entity}`}
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={problem !== null || !chosenAccountId}
          onClick={() => save.mutate()}
        >
          Pagar
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <p className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 px-3 py-2 text-sm">
          Debes <Money amount={debt.balance} className="font-semibold" />
        </p>

        <Field
          label="A capital"
          htmlFor="capital"
          hint="Esto es lo único que baja la deuda."
        >
          <input
            id="capital"
            className="ed-input ed-money"
            inputMode="numeric"
            value={principalText}
            onChange={(e) => setPrincipalText(e.target.value)}
            placeholder="300.000"
            autoFocus
            autoComplete="off"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Intereses" htmlFor="intereses">
            <input
              id="intereses"
              className="ed-input ed-money"
              inputMode="numeric"
              value={interestText}
              onChange={(e) => setInterestText(e.target.value)}
              placeholder="45.000"
              autoComplete="off"
            />
          </Field>

          <Field label="Seguro" htmlFor="seguro">
            <input
              id="seguro"
              className="ed-input ed-money"
              inputMode="numeric"
              value={insuranceText}
              onChange={(e) => setInsuranceText(e.target.value)}
              placeholder="12.000"
              autoComplete="off"
            />
          </Field>

          <Field label="Comisiones" htmlFor="comisiones">
            <input
              id="comisiones"
              className="ed-input ed-money"
              inputMode="numeric"
              value={feesText}
              onChange={(e) => setFeesText(e.target.value)}
              placeholder="3.000"
              autoComplete="off"
            />
          </Field>
        </div>

        {total > 0 && (
          <div className="space-y-2 rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-ed-text-muted">Sale de tu cuenta</span>
              <Money amount={total} className="font-semibold" />
            </div>
            <div className="flex justify-between">
              <span className="text-ed-text-muted">Baja la deuda</span>
              <Money amount={principal} className="font-semibold text-ed-success" />
            </div>
            <div className="flex justify-between">
              <span className="text-ed-text-muted">Es gasto del mes</span>
              <Money amount={interest + insurance + fees} className="font-semibold text-ed-coral-light" />
            </div>
            <div className="flex justify-between border-t border-ed-bg-border/30 pt-2">
              <span className="text-ed-text-muted">Te quedará debiendo</span>
              <Money amount={balanceAfter} className="font-semibold" />
            </div>
          </div>
        )}

        <Field label="¿De qué cuenta sale?" htmlFor="cuenta-pago">
          <select
            id="cuenta-pago"
            className="ed-input"
            value={chosenAccountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Elige una cuenta…</option>
            {(accounts.data?.accounts ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} · {formatCOP(account.balance)}
              </option>
            ))}
          </select>
        </Field>

        {problem && <ErrorNote message={problem} />}
        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}

/** El simulador: cuándo quedas libre y qué estrategia te conviene. */
function SimulatorModal({ onClose }: { onClose: () => void }): React.JSX.Element {
  const [extraText, setExtraText] = useState('');
  const extra = parseAmountFromText(extraText) ?? 0;

  const comparison = useQuery({
    queryKey: ['debts', 'simulate', extra],
    queryFn: () => simulateDebts(extra),
  });

  const data = comparison.data;

  return (
    <Modal open onClose={onClose} title="¿Cuándo quedarás libre?">
      <div className="space-y-4">
        <Field
          label="Dinero extra al mes (opcional)"
          htmlFor="extra"
          hint="Lo que podrías destinar además de las cuotas. Déjalo vacío para ver el plan actual."
        >
          <input
            id="extra"
            className="ed-input ed-money"
            inputMode="numeric"
            value={extraText}
            onChange={(e) => setExtraText(e.target.value)}
            placeholder="200.000"
            autoComplete="off"
          />
        </Field>

        {comparison.isLoading && <Loading label="Calculando el plan…" />}

        {comparison.isError && (
          <ErrorNote message="No se pudo simular. Comprueba que tengas deudas activas con saldo." />
        )}

        {data && (
          <>
            {(data.snowball.unpayable || data.avalanche.unpayable) && (
              <ErrorNote
                message="Con las cuotas actuales no se cubren ni los intereses."
                hint="La deuda crecería sola. Prueba a añadir dinero extra al mes, o renegocia la cuota con el banco."
              />
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-ed border border-ed-cyan/30 bg-ed-cyan/5 p-3">
                <p className="flex items-center gap-1.5 font-display text-sm font-bold text-ed-cyan-light">
                  <Snowflake size={14} aria-hidden="true" />
                  Bola de nieve
                </p>
                <p className="mt-1 text-xs text-ed-text-muted">Primero la deuda más pequeña</p>
                <p className="mt-2 font-display text-lg font-bold">
                  {data.snowball.months !== null
                    ? `${data.snowball.months} ${data.snowball.months === 1 ? 'mes' : 'meses'}`
                    : 'Sin fecha'}
                </p>
                <p className="text-xs text-ed-text-muted">
                  Intereses: <Money amount={data.snowball.totalInterest} />
                </p>
                {data.snowball.payoffMonthKey && (
                  <p className="text-xs text-ed-text-muted capitalize">
                    Libre en {formatMonthLabel(data.snowball.payoffMonthKey)}
                  </p>
                )}
              </div>

              <div className="rounded-ed border border-ed-gold/30 bg-ed-gold/5 p-3">
                <p className="flex items-center gap-1.5 font-display text-sm font-bold text-ed-gold">
                  <TrendingDown size={14} aria-hidden="true" />
                  Avalancha
                </p>
                <p className="mt-1 text-xs text-ed-text-muted">Primero la deuda más cara</p>
                <p className="mt-2 font-display text-lg font-bold">
                  {data.avalanche.months !== null
                    ? `${data.avalanche.months} ${data.avalanche.months === 1 ? 'mes' : 'meses'}`
                    : 'Sin fecha'}
                </p>
                <p className="text-xs text-ed-text-muted">
                  Intereses: <Money amount={data.avalanche.totalInterest} />
                </p>
                {data.avalanche.payoffMonthKey && (
                  <p className="text-xs text-ed-text-muted capitalize">
                    Libre en {formatMonthLabel(data.avalanche.payoffMonthKey)}
                  </p>
                )}
              </div>
            </div>

            <Note>{data.recommendation}</Note>

            {/* Orden de pago recomendado */}
            {data.avalanche.payoffOrder.length > 1 && (
              <div>
                <p className="ed-label mb-2">En qué orden saldarlas (avalancha)</p>
                <ol className="space-y-1.5">
                  {data.avalanche.payoffOrder.map((entry, index) => (
                    <li key={entry.id} className="flex items-center gap-2 text-sm">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ed-bg-elevated text-xs font-bold text-ed-gold">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{entry.entity}</span>
                      <span className="shrink-0 text-xs capitalize text-ed-text-muted">
                        {formatMonthLabel(entry.monthKey)}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <p className="flex items-start gap-1.5 text-xs text-ed-text-muted">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
              Es una estimación basada en tus cuotas y tus intereses de hoy. Si cambias de hábitos o
              de cuota, cambia el plan.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
